/**
 * UpcomingLessonReminderBanner — subtle Learner Hub banner that surfaces
 * the next confirmed lesson within 72 hours.
 *
 * Owner directive (Apr 30, 2026):
 *   - non-intrusive (no blocking popups)
 *   - clearly visible but not disruptive
 *   - auto-dismiss when lesson is completed or cancelled
 *
 * The banner pulls from /api/lesson-payments/my-bookings/all and renders only
 * when at least one booking has the backend-flagged `reminder_active` field
 * (lesson is confirmed and within the 72h window).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CalendarClock, ChevronRight } from "lucide-react";
import AsyncSurface from "../AsyncSurface";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const s = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${s}` : `${h12}:${String(m).padStart(2, "0")}${s}`;
};

function formatWhen(dateStr, timeStr, hoursUntil) {
  const today = new Date();
  const d = new Date(dateStr + "T00:00:00");
  const isToday = d.toDateString() === today.toDateString();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const dayLabel = isToday
    ? "Today"
    : isTomorrow
    ? "Tomorrow"
    : d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" });

  // Soft countdown — feels human, not alarmist.
  let countdown = "";
  if (hoursUntil != null) {
    if (hoursUntil < 1) countdown = "less than an hour away";
    else if (hoursUntil < 24) countdown = `${Math.round(hoursUntil)} hours away`;
    else countdown = `${Math.floor(hoursUntil / 24)} day${Math.floor(hoursUntil / 24) === 1 ? "" : "s"} away`;
  }
  return { dayLabel, time: fmtTime(timeStr), countdown };
}

/**
 * Phase 5 — Operational Continuity Convergence v2 (Feb 28, 2026).
 *
 * Doctrine pin (operator directive Phase 5):
 *   "If two surfaces answer the same psychological question, one of
 *    them should defer silently."
 *
 * The HomeworkContinuityBridge owns the "Last time → This time"
 * forward-continuity bridge. When this banner mounts alongside the
 * bridge (the canonical CalmLearnerDashboard case), the banner's
 * own continuity echo would duplicate that psychological question.
 * The banner therefore accepts `suppressContinuityLine` so the
 * parent can defer this surface silently. The banner remains a
 * calm calendar reminder (when/time/countdown) — its primary
 * psychological role.
 */
export default function UpcomingLessonReminderBanner({
  suppressContinuityLine = false,
} = {}) {
  const nav = useNavigate();
  const [next, setNext] = useState(null);
  // Phase 1.3 alive-contract migration (May 19, 2026) — the previous
  // implementation swallowed every fetch failure into silent null,
  // making "no upcoming lesson" and "the bookings API failed"
  // identical. We now track error explicitly so a learner who DOES
  // have an upcoming lesson gets a truthful surface rather than the
  // calm-baseline silence reserved for "nothing upcoming".
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Pupil Operational L3 Convergence Batch L3.1 (Feb 28, 2026) —
  // Break #1: hold continuity strings derived from the existing
  // /api/learner-framework/me response so the banner can echo
  // "Last time / This time" without inventing a new endpoint.
  // Each string is null when its source field is missing, in which
  // case the line is silent (no fallback copy).
  const [continuity, setContinuity] = useState({ last: null, next: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(
        `${API}/api/lesson-payments/my-bookings/all`,
        { withCredentials: true }
      );
      const upcoming = (data?.bookings || [])
        .filter((b) => b.reminder_active)
        .sort((a, b) => (a.hours_until_lesson || 0) - (b.hours_until_lesson || 0));
      setNext(upcoming[0] || null);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Couldn't load your upcoming lesson just now."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // L3.1 Break #1 — best-effort continuity fetch. Reuses the same
  // /api/learner-framework/me response the framework strip already
  // loads on /learn. Failures stay silent: the banner still works
  // as a calendar reminder when this continuity hint is unavailable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/learner-framework/me`,
          { withCredentials: true },
        );
        if (cancelled) return;
        // "Last time" — derive from the current_stage's most recent
        // released lesson (already in the rolled-up response via
        // current_stage.next_lesson_title and the current focus).
        // We use the prior focus when present, otherwise the current
        // skill_progress most recently touched.
        const skill = Array.isArray(data?.skill_progress)
          ? [...data.skill_progress]
              .filter((r) => r?.last_signal_at)
              .sort((a, b) => String(b.last_signal_at).localeCompare(
                String(a.last_signal_at),
              ))[0]
          : null;
        const lastTitle = (skill?.title || "").trim() || null;
        // "This time" — derive from current_stage's next lesson
        // (deterministic, lives on the same response).
        const nextTitle = (data?.current_stage?.next_lesson_title || "").trim() || null;
        setContinuity({ last: lastTitle, next: nextTitle });
      } catch {
        // Silent — calendar reminder still renders.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    load().finally(() => { if (cancelled) { /* noop */ } });
    return () => { cancelled = true; };
  }, [load]);

  // Calm-baseline silence (§18) when fetch SUCCEEDED but there is
  // nothing upcoming. Doctrine: nudges fade silently.
  if (!error && !loading && !next) return null;

  // Truthful failure surface — distinguishable from silent empty.
  if (error && !next) {
    return (
      <AsyncSurface
        testid="upcoming-lesson"
        error={error}
        errorLabel={error}
        onRetry={load}
      >
        {null}
      </AsyncSurface>
    );
  }

  // Loading flash is suppressed — the banner is a nudge, not a card,
  // so a momentary skeleton would add visual noise. We render only
  // once `next` is known.
  if (!next) return null;

  const { dayLabel, time, countdown } = formatWhen(
    next.date,
    next.start_time,
    next.hours_until_lesson
  );

  return (
    <button
      type="button"
      data-testid="upcoming-lesson-reminder-banner"
      onClick={() => nav("/learn/book")}
      className="w-full text-left relative bg-white dark:bg-slate-800 rounded-2xl border border-amber-200/80 dark:border-amber-900/40 overflow-hidden hover:bg-amber-50/40 dark:hover:bg-amber-900/10 transition-colors"
    >
      {/* Calm accent stripe — reads as reminder, not alarm */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" />
      <div className="p-4 pl-5 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
          <CalendarClock className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-600 dark:text-amber-400">
            Lesson reminder
          </p>
          <p
            data-testid="reminder-banner-when"
            className="text-sm font-extrabold text-slate-900 dark:text-white truncate"
          >
            {dayLabel} · {time}
          </p>
          {countdown && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400">{countdown}</p>
          )}
          {/* Pupil L3.1 Break #1 — calm continuity line. Renders
              only when AT LEAST ONE of `last` / `next` is non-empty.
              No fallback copy, no nag.
              Phase 5 deference: silent when `suppressContinuityLine`
              is true — the HomeworkContinuityBridge below owns the
              same psychological question ("what are we building
              on?") and this surface defers to keep the home calm. */}
          {!suppressContinuityLine && (continuity.last || continuity.next) && (
            <p
              data-testid="reminder-banner-continuity"
              className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate"
            >
              {continuity.last && (
                <>
                  <span className="font-bold text-slate-600 dark:text-slate-300">
                    Last time:
                  </span>{" "}
                  {continuity.last}
                </>
              )}
              {continuity.last && continuity.next && " · "}
              {continuity.next && (
                <>
                  <span className="font-bold text-slate-600 dark:text-slate-300">
                    This time:
                  </span>{" "}
                  {continuity.next}
                </>
              )}
            </p>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
      </div>
    </button>
  );
}
